import { FileText, Image as ImageIcon } from "lucide-react";
import type { Ultimo } from "@/api/preventivo";
import {
  ANNULLA, COLLEGAMENTO_NON_RIUSCITO, conteggio, NUOVO_PREVENTIVO, bolletteRicevute, nomiCondomini, ultimoCaricamento,
} from "./scritte";
import { AGGIUNGI_ALTRO, AGGIUNGI_ULTIMO, NON_INDICATO, ULTIMO_PREVENTIVO } from "./scritteLista";
import { AvvisoErrore } from "./BolletteFuori";

interface Props {
  /** I file arrivati dal menù «Condividi», come arrivano. */
  file: { nome: string; tipo: string }[];
  /** L'ultimo preventivo con bollette: assente = il blocco e il suo tasto non ci sono. */
  ultimo?: Ultimo | null;
  /** Ci sono altri preventivi oltre all'ultimo: solo allora «Aggiungi a un altro preventivo» serve. */
  altri: boolean;
  onUltimo: () => void;
  onAltro: () => void;
  onNuovo: () => void;
  onAnnulla: () => void;
  /** Sta partendo «Nuovo preventivo»: le scelte sono spente. */
  occupato?: boolean;
  /** L'ultimo preventivo è già al massimo: «Aggiungi all'ultimo» è spento e la frase sta sotto. */
  tetto?: string;
  /** «Nuovo preventivo» non è riuscito: la scelta resta. */
  errore?: boolean;
}

const SCELTA =
  "inline-flex h-12 w-full items-center justify-center rounded-lg border border-border bg-card px-4 text-sm font-semibold text-text cursor-pointer touch-manipulation transition-colors enabled:hover:bg-border/60 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

/** Schermata a sé (mockup parte 5): i file in arrivo, l'ultimo preventivo, tre scelte dello stesso peso (nessuna è la principale). */
export function SceltaCondivisi({ file, ultimo, altri, onUltimo, onAltro, onNuovo, onAnnulla, occupato, tetto, errore }: Props) {
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-5">
      <div>
        <p className="text-sm text-muted-foreground">Preventivo condomini</p>
        <h1 className="text-2xl font-semibold text-text">{bolletteRicevute(file.length)}</h1>
      </div>

      <ul className="divide-y divide-border rounded-xl border border-border bg-card">
        {file.map((f, i) => (
          <li key={i} className="flex items-center gap-3 px-3 py-3">
            {f.tipo.startsWith("image/")
              ? <ImageIcon className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              : <FileText className="h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />}
            <span className="min-w-0 break-words text-[15px] font-medium text-text">{f.nome}</span>
          </li>
        ))}
      </ul>

      <div className="space-y-5">
        {ultimo && (
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-text">{ULTIMO_PREVENTIVO}</p>
            <p className={ultimo.amministratore ? "text-sm font-medium text-text" : "text-sm font-medium italic text-muted-foreground"}>
              {ultimo.amministratore ?? NON_INDICATO}
            </p>
            <p className="text-sm font-medium text-text tabular-nums">{conteggio(ultimo.totale, ultimo.condomini.length)}</p>
            {ultimo.condomini.length > 0 && <p className="text-sm text-muted-foreground">{nomiCondomini(ultimo.condomini)}</p>}
            {ultimo.ultimo_caricamento && <p className="text-sm text-muted-foreground">{ultimoCaricamento(ultimo.ultimo_caricamento)}</p>}
          </div>
        )}

        <div className="space-y-2">
          {ultimo && (
            <div className="space-y-1">
              <button type="button" onClick={onUltimo} disabled={occupato || tetto !== undefined} className={SCELTA}>{AGGIUNGI_ULTIMO}</button>
              <p aria-live="polite" className="text-center text-xs text-muted-foreground empty:hidden">{tetto}</p>
            </div>
          )}
          {altri && <button type="button" onClick={onAltro} disabled={occupato} className={SCELTA}>{AGGIUNGI_ALTRO}</button>}
          <button
            type="button"
            onClick={onNuovo}
            disabled={occupato}
            aria-busy={occupato || undefined}
            className={SCELTA}
          >
            {NUOVO_PREVENTIVO}
          </button>
          {errore && <AvvisoErrore testo={COLLEGAMENTO_NON_RIUSCITO} />}
          <button
            type="button"
            onClick={onAnnulla}
            disabled={occupato}
            className="inline-flex h-11 w-full items-center justify-center rounded-lg text-sm font-medium text-muted-foreground cursor-pointer touch-manipulation transition-colors enabled:hover:text-text enabled:hover:bg-border/40 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            {ANNULLA}
          </button>
        </div>
      </div>
    </div>
  );
}
